// AboutAPIC.jsx
import React from 'react';
import {
  Building2,
  Factory,
  Users,
  Rocket,
  ArrowRight,
  Sparkles,
  Shield,
  Zap,
  Globe,
  Award,
  Cpu,
  Microscope,
  BookOpen,
  Target,
  Heart,
  Eye,
  Lightbulb,
  Clock,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { FaLinkedin, FaTwitter, FaYoutube } from "react-icons/fa";
import './AboutAPIC.css';
import Navbar from './Navbar';
import CTA from './CTA';
import Footer from './Footer';

const AboutAPIC = () => {
  const stats = [
    { value: "50+", label: "Facilities", icon: Building2 },
    { value: "2,500+", label: "Innovators", icon: Users },
    { value: "150+", label: "Machines", icon: Cpu },
    { value: "12", label: "Centres", icon: MapPin }
  ];

  const values = [
    {
      icon: Lightbulb,
      title: "Innovation First",
      description: "We believe in pushing boundaries and enabling breakthrough innovations."
    },
    {
      icon: Users,
      title: "Collaborative Ecosystem",
      description: "Building bridges between innovators, experts, and facilities."
    },
    {
      icon: Shield,
      title: "Trust & Quality",
      description: "Ensuring high standards and reliable manufacturing partnerships."
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Connecting local capabilities with global opportunities."
    }
  ];

  const milestones = [
    { year: "2020", title: "Founded", description: "APIC ProtoHub was established" },
    { year: "2021", title: "First Facility", description: "First manufacturing facility onboarded" },
    { year: "2022", title: "1,000 Innovators", description: "Reached 1,000 registered innovators" },
    { year: "2023", title: "Network Growth", description: "Expanded to 12 centres across AP" }
  ];

  return (
    <>
    <Navbar/>
    <section className="about-apic">
      {/* ========== HERO/BANNER SECTION - FULL WIDTH ========== */}
      <div className="about-hero">
        {/* Background Image */}
        <div className="about-hero-background">
          <img
            src="https://images.unsplash.com/photo-1581092335871-5f2b5e17cd7a?w=1600&q=80"
            alt="APIC ProtoHub facility"
            className="about-hero-bg-image"
            loading="eager"
          />
          <div className="about-hero-overlay"></div>
          <div className="about-hero-glow"></div>
        </div>

        {/* Content */}
        <div className="about-hero-content">
          <div className="about-hero-left">
            <div className="about-badge">
              <Sparkles size={16} className="about-badge-icon" />
              <span>About APIC ProtoHub</span>
            </div>

            <h1 className="about-hero-title">
              <span className="about-hero-title-line1">Empowering Innovation</span>
              <span className="about-hero-title-line2">Across Andhra Pradesh</span>
            </h1>

            <p className="about-hero-description">
              APIC ProtoHub is a first-of-its-kind initiative connecting manufacturing 
              facilities, innovators, and experts to accelerate innovation and 
              manufacturing in Andhra Pradesh.
            </p>

            <div className="about-hero-stats">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <React.Fragment key={index}>
                    <div className="about-hero-stat">
                      <div className="about-hero-stat-icon">
                        <Icon size={18} />
                      </div>
                      <div>
                        <p className="about-hero-stat-value">{stat.value}</p>
                        <p className="about-hero-stat-label">{stat.label}</p>
                      </div>
                    </div>
                    {index < stats.length - 1 && (
                      <div className="about-hero-stat-divider"></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="about-hero-right">
            <div className="about-hero-image-wrapper">
              <img
                src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&h=600&fit=crop&crop=center&auto=format&q=80"
                alt="Advanced manufacturing facility"
                className="about-hero-image"
                loading="lazy"
              />
              <div className="about-hero-float-badge">
                <Award size={18} className="about-float-icon" />
                <div>
                  <p className="about-float-title">Innovation Hub</p>
                  <p className="about-float-sub">Recognized by Govt. of AP</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== CONTENT - CONTAINED IN 1200px ========== */}
      <div className="about-container">
        {/* ========== MISSION & VISION ========== */}
        <div className="about-mission-vision">
          <div className="about-mv-card">
            <div className="about-mv-icon-wrapper">
              <div className="about-mv-icon">
                <Target size={24} />
              </div>
            </div>
            <h3 className="about-mv-title">Our Mission</h3>
            <p className="about-mv-desc">
              To create a vibrant innovation ecosystem that connects manufacturing 
              capabilities with innovators, enabling rapid prototyping and product 
              development across Andhra Pradesh.
            </p>
          </div>

          <div className="about-mv-card featured">
            <div className="about-mv-icon-wrapper">
              <div className="about-mv-icon">
                <Eye size={24} />
              </div>
            </div>
            <h3 className="about-mv-title">Our Vision</h3>
            <p className="about-mv-desc">
              To position Andhra Pradesh as a global hub for innovation and 
              manufacturing excellence by 2025, empowering 10,000+ innovators 
              and 200+ facilities.
            </p>
            <div className="about-mv-badge">2030 Goal</div>
          </div>

          <div className="about-mv-card">
            <div className="about-mv-icon-wrapper">
              <div className="about-mv-icon">
                <Heart size={24} />
              </div>
            </div>
            <h3 className="about-mv-title">Our Values</h3>
            <p className="about-mv-desc">
              Innovation, collaboration, integrity, and excellence drive everything 
              we do at APIC ProtoHub.
            </p>
          </div>
        </div>

        {/* ========== WHAT WE DO ========== */}
        <div className="about-what-we-do">
          <div className="about-section-header">
            <div className="about-section-badge">
              <Zap size={14} />
              <span>What We Do</span>
            </div>
            <h2 className="about-section-title">
              Building Bridges Between <span>Innovators & Industry</span>
            </h2>
            <p className="about-section-desc">
              APIC ProtoHub provides a comprehensive platform for innovation and manufacturing
            </p>
          </div>

          <div className="about-services-grid">
            <div className="about-service-card">
              <div className="about-service-icon">
                <Building2 size={24} />
              </div>
              <h4 className="about-service-title">Facility Registration</h4>
              <p className="about-service-desc">
                Showcase your manufacturing capabilities to a wider innovation ecosystem
              </p>
            </div>

            <div className="about-service-card">
              <div className="about-service-icon">
                <Users size={24} />
              </div>
              <h4 className="about-service-title">Innovator Connect</h4>
              <p className="about-service-desc">
                Connect innovators with the right experts and manufacturing resources
              </p>
            </div>

            <div className="about-service-card">
              <div className="about-service-icon">
                <Microscope size={24} />
              </div>
              <h4 className="about-service-title">Prototype Development</h4>
              <p className="about-service-desc">
                End-to-end support for turning ideas into manufacturable products
              </p>
            </div>

            <div className="about-service-card">
              <div className="about-service-icon">
                <BookOpen size={24} />
              </div>
              <h4 className="about-service-title">Knowledge Sharing</h4>
              <p className="about-service-desc">
                Access technical expertise, best practices, and industry insights
              </p>
            </div>
          </div>
        </div>

        {/* ========== VALUES ========== */}
        <div className="about-values">
          <div className="about-section-header">
            <div className="about-section-badge">
              <Award size={14} />
              <span>Core Values</span>
            </div>
            <h2 className="about-section-title">
              What Drives <span>Our Ecosystem</span>
            </h2>
          </div>

          <div className="about-values-grid">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="about-value-card">
                  <div className="about-value-icon">
                    <Icon size={24} />
                  </div>
                  <h4 className="about-value-title">{value.title}</h4>
                  <p className="about-value-desc">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ========== MILESTONES ========== */}
        <div className="about-milestones">
          <div className="about-section-header">
            <div className="about-section-badge">
              <Clock size={14} />
              <span>Journey</span>
            </div>
            <h2 className="about-section-title">
              Our <span>Milestones</span>
            </h2>
          </div>

          <div className="about-milestones-timeline">
            {milestones.map((milestone, index) => (
              <div key={index} className="about-milestone-item">
                <div className="about-milestone-year">{milestone.year}</div>
                <h4 className="about-milestone-title">{milestone.title}</h4>
                <p className="about-milestone-desc">{milestone.description}</p>
              </div>
            ))}
          </div>
        </div>

        
      </div>
    </section>
   
    <CTA />
 <Footer />

 </>
  );
};

export default AboutAPIC;